import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useStudents, useDeleteStudent } from '../lib/hooks';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../components/ui/pagination';
import { 
  Plus, 
  Search, 
  Users, 
  Loader2, 
  MoreHorizontal,
  Eye,
  Pencil,
  UserX,
  UserCheck,
  Trash2,
  Filter,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import StudentForm from '../components/students/StudentForm';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../lib/translations';

const levelColors = {
  debutant: 'bg-slate-100 text-slate-700',
  A1: 'bg-slate-200 text-slate-800',
  A2: 'bg-blue-100 text-blue-700',
  B1: 'bg-yellow-100 text-yellow-700',
  B2: 'bg-orange-100 text-orange-700',
  C1: 'bg-green-100 text-green-700',
  C2: 'bg-emerald-100 text-emerald-700',
};

const statusColors = {
  pending: 'bg-slate-100 text-slate-600',
  active: 'bg-success/10 text-success',
  completed: 'bg-blue-100 text-blue-700',
  suspended: 'bg-destructive/10 text-destructive',
};

const statusLabels = {
  pending: 'En attente',
  active: 'Actif',
  completed: 'Terminé',
  suspended: 'Suspendu',
};

const visaLabels = {
  studies: 'Études',
  professional: 'Formation pro',
  work: 'Travail',
  family: 'Regroupement familial',
};

const StudentsPage = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  
  // RBAC permissions
  const isAdmin = user?.role === 'admin';
  const isRespPeda = user?.role === 'resp_peda';
  const isRespAdmin = user?.role === 'resp_admin';
  const canCreate = isAdmin; // Only admin can create
  const canEdit = isAdmin || isRespPeda || isRespAdmin; // Admin, resp_peda, resp_admin can edit
  const canDelete = isAdmin; // Only admin can delete
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    level: '',
    visa_objective: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);

  const { data, isLoading, error } = useStudents({ 
    page, 
    per_page: 20,
    search: filters.search || undefined,
    status: filters.status || undefined,
    level: filters.level || undefined,
    visa_objective: filters.visa_objective || undefined,
  });

  const deleteStudent = useDeleteStudent();

  const handleOpenForm = (student = null) => {
    setSelectedStudent(student);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setSelectedStudent(null);
  };

  const handleDelete = async () => {
    if (!studentToDelete) return;
    try {
      await deleteStudent.mutateAsync(studentToDelete.id);
      toast.success('Étudiant supprimé avec succès');
      setDeleteDialogOpen(false);
      setStudentToDelete(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetFilters = () => {
    setFilters({ search: '', status: '', level: '', visa_objective: '' });
    setPage(1);
  };

  const hasActiveFilters = filters.status || filters.level || filters.visa_objective;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="students-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="font-heading text-3xl font-bold text-foreground">{t('students_title', lang)}</h1>
          {data && (
            <Badge variant="secondary" className="text-base px-3">
              {data.total}
            </Badge>
          )}
        </div>
        {canCreate && (
          <Button onClick={() => handleOpenForm()} className="btn-active" data-testid="add-student-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouvel étudiant
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* Search and filter toggle */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={t("students_search", lang)}
                  value={filters.search}
                  onChange={(e) => {
                    setFilters({ ...filters, search: e.target.value });
                    setPage(1);
                  }}
                  className="pl-10"
                  data-testid="student-search-input"
                />
              </div>
              <Button 
                variant="outline" 
                onClick={() => setShowFilters(!showFilters)}
                className={hasActiveFilters ? 'border-primary text-primary' : ''}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filtres
                {hasActiveFilters && <Badge className="ml-2 bg-primary text-white">!</Badge>}
                {showFilters ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
              </Button>
            </div>

            {/* Expandable filters */}
            {showFilters && (
              <div className="flex flex-col md:flex-row gap-4 pt-4 border-t animate-slide-up">
                <Select 
                  value={filters.level} 
                  onValueChange={(value) => {
                    setFilters({ ...filters, level: value });
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-full md:w-40" data-testid="level-filter">
                    <SelectValue placeholder="Niveau" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('all_levels', lang)}</SelectItem>
                    <SelectItem value="debutant">Débutant</SelectItem>
                    <SelectItem value="A1">A1</SelectItem>
                    <SelectItem value="A2">A2</SelectItem>
                    <SelectItem value="B1">B1</SelectItem>
                    <SelectItem value="B2">B2</SelectItem>
                    <SelectItem value="C1">C1</SelectItem>
                  </SelectContent>
                </Select>

                <Select 
                  value={filters.status} 
                  onValueChange={(value) => {
                    setFilters({ ...filters, status: value });
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-full md:w-40" data-testid="status-filter">
                    <SelectValue placeholder="Statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous statuts</SelectItem>
                    <SelectItem value="pending">{t('status_pending', lang)}</SelectItem>
                    <SelectItem value="active">{t('status_active', lang)}</SelectItem>
                    <SelectItem value="completed">{t('status_completed', lang)}</SelectItem>
                    <SelectItem value="suspended">{t('status_suspended', lang)}</SelectItem>
                  </SelectContent>
                </Select>

                <Select 
                  value={filters.visa_objective} 
                  onValueChange={(value) => {
                    setFilters({ ...filters, visa_objective: value });
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-full md:w-48" data-testid="visa-filter">
                    <SelectValue placeholder="Objectif visa" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous objectifs</SelectItem>
                    <SelectItem value="studies">{lang === 'de' ? 'Studium' : 'Études'}</SelectItem>
                    <SelectItem value="professional">Formation pro</SelectItem>
                    <SelectItem value="work">{lang === 'de' ? 'Arbeit' : 'Travail'}</SelectItem>
                    <SelectItem value="family">{lang === 'de' ? 'Familienzusammenführung' : 'Regroupement familial'}</SelectItem>
                  </SelectContent>
                </Select>

                <Button variant="ghost" onClick={resetFilters} className="text-muted-foreground">
                  <X className="w-4 h-4 mr-2" />
                  Réinitialiser
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="text-center py-12 text-destructive">
              <p>Erreur lors du chargement des étudiants</p>
            </div>
          ) : data?.students?.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-heading text-xl font-semibold mb-1">Aucun étudiant trouvé</h3>
              <p className="text-muted-foreground mb-4">
                {hasActiveFilters || filters.search 
                  ? "Modifiez vos filtres pour voir plus de résultats"
                  : "Ajoutez votre premier étudiant pour commencer"
                }
              </p>
              {!hasActiveFilters && !filters.search && canCreate && (
                <Button onClick={() => handleOpenForm()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un étudiant
                </Button>
              )}
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('students_title', lang) === 'Studierende' ? 'Studierende/r' : 'Étudiant'}</TableHead>
                    <TableHead>{t('students_level', lang)}</TableHead>
                    <TableHead className="hidden md:table-cell">{t('dossier_detail_visa', lang)}</TableHead>
                    <TableHead>{t('students_status', lang)}</TableHead>
                    <TableHead className="hidden lg:table-cell">{lang === 'de' ? 'Akte' : 'Dossier'}</TableHead>
                    <TableHead className="hidden lg:table-cell">Inscription</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.students?.map((student) => (
                    <TableRow key={student.id} className="hover:bg-muted/50 cursor-pointer" onClick={() => navigate(`/students/${student.id}`)}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-semibold">
                            {student.first_name[0]}{student.last_name[0]}
                          </div>
                          <div>
                            <p className="font-medium">{student.first_name} {student.last_name}</p>
                            <p className="text-sm text-muted-foreground">{student.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={levelColors[student.german_level] || levelColors.A1}>
                          {student.german_level || 'A1'}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <Badge variant="outline">
                          {visaLabels[student.visa_objective] || student.visa_objective}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[student.enrollment_status]}>
                          {statusLabels[student.enrollment_status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="flex items-center gap-2 min-w-[100px]">
                          <Progress 
                            value={student.documents_count > 0 ? (student.documents_validated / student.documents_count) * 100 : 0} 
                            className="h-2 w-16" 
                          />
                          <span className="text-xs text-muted-foreground">
                            {student.documents_validated}/{student.documents_count || 0}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-muted-foreground">
                        {formatDate(student.created_at)}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`student-actions-${student.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/students/${student.id}`)}>
                              <Eye className="w-4 h-4 mr-2" />
                              Voir
                            </DropdownMenuItem>
                            {canEdit && (
                              <DropdownMenuItem onClick={() => handleOpenForm(student)}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Modifier
                              </DropdownMenuItem>
                            )}
                            {canDelete && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => {
                                    setStudentToDelete(student);
                                    setDeleteDialogOpen(true);
                                  }}
                                  className="text-destructive"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Supprimer
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {data?.total_pages > 1 && (
                <div className="flex items-center justify-between p-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    {data.total} étudiants trouvés
                  </p>
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => setPage(Math.max(1, page - 1))}
                          className={page === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                      {Array.from({ length: Math.min(5, data.total_pages) }, (_, i) => {
                        let pageNum;
                        if (data.total_pages <= 5) {
                          pageNum = i + 1;
                        } else if (page <= 3) {
                          pageNum = i + 1;
                        } else if (page >= data.total_pages - 2) {
                          pageNum = data.total_pages - 4 + i;
                        } else {
                          pageNum = page - 2 + i;
                        }
                        return (
                          <PaginationItem key={pageNum}>
                            <PaginationLink
                              onClick={() => setPage(pageNum)}
                              isActive={page === pageNum}
                              className="cursor-pointer"
                            >
                              {pageNum}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      })}
                      <PaginationItem>
                        <PaginationNext 
                          onClick={() => setPage(Math.min(data.total_pages, page + 1))}
                          className={page === data.total_pages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Student Form Sheet */}
      <StudentForm 
        open={formOpen} 
        onClose={handleCloseForm} 
        student={selectedStudent} 
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer {studentToDelete?.first_name} {studentToDelete?.last_name}? 
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentsPage;
